import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { useEffect } from "react"
import {
    COMMAND_PRIORITY_EDITOR,
    COMMAND_PRIORITY_LOW,
    createCommand,
    LexicalCommand,
    $isNodeSelection,
    SELECTION_CHANGE_COMMAND,
    $getSelection,
    $isRangeSelection,
    $isElementNode
} from "lexical"
import { mergeRegister } from "@lexical/utils"
import { $createBoxColorNode, $isBoxColorNode, BoxColorNode } from "../../nodes/BoxColorNode"
import { $isDirectionalParagraphNode } from "../../nodes/DirectionalParagraphNode"
import { $isDirectionalHeadingNode } from "../../nodes/DirectionalHeadingNode"

export type InsertBoxColorPayload = {
    backgroundColor: string
    padding: string
    direction?: "ltr" | "rtl"
}

export const INSERT_BOX_COLOR_COMMAND: LexicalCommand<InsertBoxColorPayload> = createCommand("INSERT_BOX_COLOR_COMMAND")

export default function BoxColorPlugin(): JSX.Element | null {
    const [editor] = useLexicalComposerContext()

    useEffect(() => {
        if (!editor.hasNodes([BoxColorNode])) {
            throw new Error("BoxColorPlugin: BoxColorNode not registered on editor")
        }

        return mergeRegister(
            editor.registerCommand<InsertBoxColorPayload>(
                INSERT_BOX_COLOR_COMMAND,
                (payload) => {
                    editor.update(() => {
                        const selection = $getSelection()

                        if ($isRangeSelection(selection)) {
                            const nodes = selection.getNodes()

                            if (nodes.length === 0) return false

                            // Get the parent paragraph node
                            const firstNode = nodes[0]
                            const paragraphNode = $isElementNode(firstNode) ? firstNode : firstNode.getParentOrThrow()

                            // Normal case - setting a color
                            if ($isBoxColorNode(paragraphNode)) {
                                // If already wrapped in BoxColorNode, just update the color
                                const boxColorNode = paragraphNode as BoxColorNode
                                boxColorNode.setBackgroundColor(payload.backgroundColor)
                                boxColorNode.setPadding(payload.padding)
                            } else {
                                // Create new BoxColorNode and wrap the paragraph
                                const boxColorNode = $createBoxColorNode(
                                    payload.backgroundColor,
                                    payload.padding,
                                    payload.direction // Use direction from payload if provided
                                )

                                // Get all content from the paragraph
                                const children = paragraphNode.getChildren()

                                // Move children to new BoxColorNode
                                boxColorNode.append(...children)

                                // Replace paragraph content with BoxColorNode
                                paragraphNode.append(boxColorNode)

                                // Get direction from the paragraph or its parent
                                let direction = payload.direction // First try to use direction from payload

                                // If no direction in payload, check if we're in a DirectionalHeadingNode
                                if (!direction && $isDirectionalHeadingNode(paragraphNode)) {
                                    direction = paragraphNode.getDirection()
                                }
                                // Then try to get direction from the paragraph
                                else if (!direction && $isDirectionalParagraphNode(paragraphNode)) {
                                    direction = paragraphNode.getDirection()
                                }

                                // If no direction found, try to get from parent
                                if (!direction) {
                                    const parent = paragraphNode.getParent()
                                    if (
                                        parent &&
                                        "getDirection" in parent &&
                                        typeof parent.getDirection === "function"
                                    ) {
                                        try {
                                            const parentDirection = parent.getDirection()
                                            if (parentDirection === "rtl" || parentDirection === "ltr") {
                                                direction = parentDirection
                                            }
                                        } catch (e) {
                                            // Ignore errors if getDirection is not available
                                        }
                                    }
                                }

                                // Apply direction if found
                                if (direction) {
                                    boxColorNode.setDirection(direction)

                                    // Use format from paragraph if available, otherwise use direction-based alignment
                                    const format = paragraphNode.getFormatType ? paragraphNode.getFormatType() : null
                                    if (
                                        format &&
                                        (format === "left" ||
                                            format === "right" ||
                                            format === "center" ||
                                            format === "justify")
                                    ) {
                                        boxColorNode.setFormat(format)
                                    } else {
                                        boxColorNode.setFormat(direction === "rtl" ? "right" : "left")
                                    }

                                    // Update DOM attributes
                                    const boxColorDOM = editor.getElementByKey(boxColorNode.getKey())
                                    if (boxColorDOM instanceof HTMLElement) {
                                        boxColorDOM.dir = direction
                                        boxColorDOM.style.direction = direction
                                        boxColorDOM.style.unicodeBidi = "embed"

                                        // Apply text alignment based on format or direction
                                        if (
                                            format &&
                                            (format === "left" ||
                                                format === "right" ||
                                                format === "center" ||
                                                format === "justify")
                                        ) {
                                            boxColorDOM.style.textAlign = format
                                        } else {
                                            boxColorDOM.style.textAlign = direction === "rtl" ? "right" : "left"
                                        }
                                    }
                                }
                            }
                        }
                        return true
                    })
                    return true
                },
                COMMAND_PRIORITY_EDITOR
            ),

            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    const selection = $getSelection()
                    if ($isNodeSelection(selection)) {
                        const nodes = selection.getNodes()
                        const node = nodes[0]
                        if ($isBoxColorNode(node)) {
                            return true
                        }
                    }
                    return false
                },
                COMMAND_PRIORITY_LOW
            )
        )
    }, [editor])

    return null
}
